// Text Message Component
// Following existing patterns from MessageItem

import React, { useCallback, useEffect, useState } from 'react';
import { TextMessageProps } from '../../../types/messaging';
import {
  TextMessageContainer,
  TextContent,
  TextActions,
  TextActionButton
} from '../../../styles/Messaging/MessageTypes.styles';
import { logger } from '../../../utils/logger';

export const TextMessage: React.FC<TextMessageProps> = ({
  message,
  isOwn,
  onAction
}) => {
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(message.content).catch(console.error);
  }, [message.content]);

  /**
   * Whether this message is still editable.
   *
   * Five minutes from sending, and only your own. The server enforces the same
   * window — this decides whether to OFFER the action, so nobody is invited to
   * edit something that will be refused.
   */
  const EDIT_WINDOW_MS = 5 * 60 * 1000;
  const withinEditWindow = isOwn
    && Date.now() - new Date(message.createdAt).getTime() < EDIT_WINDOW_MS;

  // Re-render as the window closes, so the pencil disappears while the message
  // is on screen instead of lingering until something else causes a render.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!withinEditWindow) return;
    const remaining = EDIT_WINDOW_MS - (Date.now() - new Date(message.createdAt).getTime());
    const timer = setTimeout(() => setTick((t) => t + 1), Math.max(1000, remaining));
    return () => clearTimeout(timer);
  }, [withinEditWindow, message.createdAt, EDIT_WINDOW_MS]);

  // Simple link detection and formatting
  const formatText = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: isOwn ? '#ffffff' : '#667eea',
              textDecoration: 'underline'
            }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <TextMessageContainer $isOwn={isOwn}>
      <TextContent>
        {formatText(message.content)}
      </TextContent>
      
      {/* These were 📋 Copy and a ✏️ that only wrote to the console — it looked
          like Edit and did nothing at all. Now: edit while the window is open,
          unsend your own message for everyone, and remove any message from
          your own view. */}
      <TextActions>
        <TextActionButton onClick={handleCopy} title="Copy">
          📋
        </TextActionButton>
        {withinEditWindow && (
          <TextActionButton
            onClick={() => onAction?.(message.id, 'edit')}
            title="Edit (5 minutes after sending)"
          >
            ✏️
          </TextActionButton>
        )}
        {isOwn && (
          <TextActionButton
            onClick={() => onAction?.(message.id, 'unsend')}
            title="Unsend for everyone"
          >
            ↩️
          </TextActionButton>
        )}
        <TextActionButton
          onClick={() => onAction?.(message.id, 'hide')}
          title="Remove from my messages"
        >
          🗑️
        </TextActionButton>
      </TextActions>
    </TextMessageContainer>
  );
};
